// src/services/probeService.ts
import { LogRecord, ProbeData } from '../models/Probe';
import userService from './userService';
import logger from '../utils/logger';

const dataPath = '/data/mock_logs/';

const API_BASE_URL = process.env.REACT_APP_PULSE_API_BASE_URL || 'http://localhost:8080';

// Format start_time and end_time to the desired string format
const formatDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

type FetchFilter = Array<{
  attribute: string;
  attribute_value: string;
}>;

export const fetchAvailableRequestIds = async (apiKey: string | null, customerId: string, services: string[], filters: FetchFilter): Promise<string[]> => {

  const baseUrl = API_BASE_URL;
  const url = new URL(`${baseUrl}/api/probe`);

  // Calculate start_time (7 days from now) and end_time (now) in UTC
  const now = new Date();
  const startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const endTime = now;

  filters = [ { attribute: "severity", attribute_value: "ERROR" }, ...filters]

  // find the request_id for requests that encountered ERROR in the past N minutes
  const requestPayload = {
    customer: customerId,
    services: services,
    filter_attrs: filters,
    output_attrs: [ "request_id"],
    start_time:formatDate(startTime),
    end_time: formatDate(endTime),
    limit: 100
  };

  // Note: Authentication headers (Authorization, X-API-Key) can be added here when required.

  try {
    const data: ProbeData = 
      await userService.authenticatedFetch2<ProbeData>(
      "fetch records for request_id",
      apiKey,
      '/api/probe',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // No query parameters
          body: JSON.stringify(requestPayload),
        },
      );

    if (data && Array.isArray(data)) {
      
      // ToDo: conduct sort and filtering to unique request_id in the server side itself

      // Sort descending by timestamp_ns (newest last)
      const sortedEvents = data.sort((a, b) => parseInt(a.timestamp_ns) - parseInt(b.timestamp_ns));

      // extract just the request_id
      const requestIds = sortedEvents.map(event => event.data.request_id);

      // find the unique set of requestIds
      const uniqueRequestIds = [...new Set(requestIds)];

      logger.info( "got events: ", sortedEvents);
      logger.info( "got requestIds: ", requestIds);
      logger.info( "got uniqueRequestIds: ", uniqueRequestIds)
      return uniqueRequestIds;
    }
    return []; // Return an empty array if data is not valid or not an array
  } catch (error) {
    const errMessage = `Could not load probe data for recent errors. Please try again later.`
    logger.error(errMessage, error);
    throw { message: errMessage}; // Re-throw the error to be handled by the caller
    // return [];
  }  
};

/**
 * Fetches the list of available mock request IDs from a manifest file.
 * This function expects a manifest.json file in the `public/data/mock_logs/` directory.
 * Removed: 7/1/2025 - since we got the main fetchAvailableRequestIds() from the server working
 */
// export const fetchAvailableRequestIdsFromMock = async (): Promise<string[]> => {
//   const recordsManifest = dataPath + "manifest.json"
//   try {
//     const response = await fetch(recordsManifest);
//     if (!response.ok) {
//       throw new Error(`Network response was not ok: ${response.statusText}`);
//     }
//     const manifest: { request_ids: string[] } = await response.json();
//     return manifest.request_ids || [];
//   } catch (error) {
//     logger.error(`Could not load mock request ID manifest. Make sure public${recordsManifest} exists.`, error);
//     return [];
//   }
// };

export const fetchProbeData = async (apiKey: string | null, customerId: string,  services: string[], requestId: string): Promise<ProbeData> => {
  const baseUrl = API_BASE_URL;
  const url = new URL(`${baseUrl}/api/probe`);

  // Calculate start_time (16 days from now) and end_time (now) in UTC
  const now = new Date();
  const startTime = new Date(now.getTime() - 16 * 24 * 60 * 60 * 1000); // 16 days from now
  const endTime = now;

  const requestPayload = {
    customer: customerId,
    services: services,
    filter_attrs: [
      { attribute: "request_id", attribute_value: requestId },
    ],
    output_attrs: [],
    start_time:formatDate(startTime),
    end_time: formatDate(endTime),
    limit: 1000
  };

  // Note: Authentication headers (Authorization, X-API-Key) can be added here when required.

  try {
    const data: ProbeData = 
      await userService.authenticatedFetch2<ProbeData>(
      "fetch records for request_id",
      apiKey,
      '/api/probe',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // No query parameters
          body: JSON.stringify(requestPayload),
        },
      );

    if (data && Array.isArray(data)) {
      // Sort descending by timestamp_ns (newest last)
      const sortedEvents = data.sort((a, b) => parseInt(a.timestamp_ns) - parseInt(b.timestamp_ns));
      return { events: sortedEvents };
    }

    return { events: [] }; // Return empty if events are not in the response
  } catch (error) {
    const errMessage = `Could not load probe data for requestId: ${requestId}. Please try again later.`
    logger.error(errMessage, error);
    // return { events: [] }; // On error, return an empty array to prevent crashing the UI.
    throw { message: errMessage}; // Re-throw the error to be handled by the caller
  }
};

export const fetchProbeDataFromMock = async (requestId: string): Promise<ProbeData> => {
  return new Promise(async (resolve) => {
    const srcRecords = dataPath + `sample.${requestId}.json`;

    try {
        // Dynamically import the mock data based on the request ID.

        const response = await fetch(srcRecords);
        if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        const logRecords: LogRecord[]  = await response.json();

      // The old logic filtered records, but now the file itself contains the relevant records.
      // We just need to sort them.
      const sortedEvents = [...logRecords].sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp));

      setTimeout(() => {
        resolve({ events: sortedEvents });
      }, 500); // Simulate API call delay

    } catch (error) {
      logger.error(`Could not load mock data for requestId: ${requestId}. Make sure a file named '${srcRecords}' exists.`, error);
      // On error (e.g., file not found), resolve with an empty array to prevent crashing the UI.
      setTimeout(() => {
        resolve({ events: [] });
      }, 500);
    }
  });
};